import React from 'react';
import { stringify } from 'query-string';
import { Helmet } from 'react-helmet';
import { Breadcrumbs } from '@saucelabs/ds-breadcrumbs';
import { Row, Col } from 'react-flexbox-grid';
import { Typography } from '@saucelabs/ds-typography';
import { InlineAlert } from '@saucelabs/ds-alert';
import { Button } from '@saucelabs/ds-button';
import { Link } from 'react-router-dom';

import BackToPlatformLink from '../components/BackToPlatformLink';
import { amountSeparator, formatThousand } from '../utils/numbers';
import { trackEvent, EVENTS, MODE_USAGE } from '../tracking';
import {
  ROUTE_PARENT_SECTION,
  CREDITS_PER_BLOCK,
  SALES_CONTACT_EMAIL,
  EMAIL_SUPPORT_PLAN_UPDATE,
} from '../constants';
import SideNavigation from './SideNavigation';
import ContainerStyles from '../components/Layout/GridContainer.styles';

interface UserPartialState {
  username: string;
  email: string;
}

export interface Props {
  action: 'update' | 'cancel';
  user: UserPartialState;
  creditBlockPrice: number;
  currentCreditBlocksAmount: number;
  hideCredits?: boolean;
}

interface EmailContentProps {
  action: Props['action'];
  user: UserPartialState;
  currentCreditBlocksAmount: Props['currentCreditBlocksAmount'];
}

const PARENT_URL = `/${ROUTE_PARENT_SECTION}`;

const INDENTATION_REGEX = /\t|[ ]{2,}/g;

const EMAIL_SUBJECT = {
  update: 'Plan Update Request',
  cancel: 'Plan Cancellation Request',
};

const CREDITS_PER_BLOCK_FORMATTED = formatThousand(CREDITS_PER_BLOCK, 'standard');

const getCrumbList = (action: Props['action']) => (
  [
    { name: 'Subscription and Billing', url: PARENT_URL },
    { name: `Request Plan ${action === EMAIL_SUPPORT_PLAN_UPDATE ? 'Change' : 'Cancellation'}` },
  ]
);

const getEmailBody = ({ action, user, currentCreditBlocksAmount }: EmailContentProps) => `
  Email: ${user.email}
  Username: ${user.username}
  Current plan: ${formatThousand(currentCreditBlocksAmount * CREDITS_PER_BLOCK, 'standard')} credits/month

  ------------------------

  PLEASE PROVIDE THE TOTAL REQUESTED CREDITS PER MONTH (in increments of ${CREDITS_PER_BLOCK_FORMATTED})*:

  ------------------------

  *Sauce Labs credits are sold in increments of ${CREDITS_PER_BLOCK_FORMATTED}. If you request credits that are not in increments of ${CREDITS_PER_BLOCK_FORMATTED}, we will increase your request to the next increment.

  By sending this email, you’re authorizing Sauce Labs to update your monthly allotment of credits and to charge your payment method on file for the additional credits. Moving forward, you’ll be charged for the new total requested credits on each month’s billing date until you change or cancel your plan.

  Thank you for being a Sauce Labs customer.
  If you have any questions, please contact billing@saucelabs.com
`;

const getEmailContent = (props: EmailContentProps) => (
  stringify({
    subject: EMAIL_SUBJECT[props.action],
    body: getEmailBody(props).replace(INDENTATION_REGEX, ''),
  })
);

const handleConfirmClick = (action: EmailContentProps['action']) => {
  const eventName = (action === EMAIL_SUPPORT_PLAN_UPDATE) ? EVENTS.USAGE_PLANS_REQUEST_CHANGE :
    EVENTS.USAGE_PLANS_REQUEST_CANCEL;

  trackEvent(eventName, MODE_USAGE);
};

const getUpdateText = (
  conditions: Pick<Props, 'action' | 'hideCredits'>,
  messages: {
    update: React.ReactNode,
    updateNoCredits?: React.ReactNode | undefined,
    cancel?: React.ReactNode
  },
): React.ReactNode => {
  if (conditions.action === 'update') {
    if (conditions.hideCredits && messages.updateNoCredits !== undefined) {
      return messages.updateNoCredits;
    }
    return messages.update;
  }
  return messages.cancel ?? '';
};

export const EmailSupport: React.FC<Props> = ({
  action,
  user,
  creditBlockPrice,
  currentCreditBlocksAmount,
  hideCredits,
}) => (
  <>
    <Helmet>
      <title>
        {action === EMAIL_SUPPORT_PLAN_UPDATE ? (
          'Request Plan Change'
        ) : (
          'Request Plan Cancellation'
        )}
      </title>
    </Helmet>
    <Breadcrumbs
      list={getCrumbList(action)}
      prefixComponent={<BackToPlatformLink />}
    >
      <SideNavigation showCreditsFAQ />
    </Breadcrumbs>

    <div className={ContainerStyles.container}>
      <Row>
        <Col
          className="mx-auto"
          lg={6}
          md={8}
        >
          <div>
            <Typography
              className="text-center text-high-emphasis mt-1"
              component="h2"
              disableSpacing
              variant="heading-1"
            >
              {action === EMAIL_SUPPORT_PLAN_UPDATE ? (
                'Email Support to Change Your Plan'
              ) : (
                'Email Support to Cancel Your Plan'
              )}
            </Typography>
            <Typography
              className="mt-3 text-center text-high-emphasis"
              component="p"
              variant="body-large"
            >
              {
                getUpdateText(
                  { action, hideCredits },
                  {
                    update: `Credits can be purchased in ${CREDITS_PER_BLOCK_FORMATTED} increments. For every ${CREDITS_PER_BLOCK_FORMATTED} credits,
                    you'll pay ${amountSeparator(creditBlockPrice)} (excluding taxes and fees).
                    You may request as many credits as you need.`,
                    updateNoCredits: '',
                    cancel: (
                      <>
                        {'We\'re sorry to see you go.'}
                        <br />
                        To cancel your plan, please contact our support team.
                      </>
                    ),
                  },
                )
              }
            </Typography>

            {
              getUpdateText(
                { action, hideCredits },
                {
                  update: (
                    <InlineAlert
                      className="mt-3"
                      color="info"
                    >
                      Upgrades take effect immediately upon change in plan.
                      Downgrades will take effect at the end of the billing cycle.
                    </InlineAlert>
                  ),
                  cancel: (
                    <InlineAlert
                      className="mt-3"
                      color="warning"
                      title="Cancellations take place at the end of the billing cycle"
                    >
                      You can continue to run tests on Sauce Labs until
                      your credits run out or expire.
                      Your account will remain accessible after cancellation.
                    </InlineAlert>
                  ),
                },
              )
            }

            <div className="flex justify-center mt-4">
              <Button
                component="a"
                href={`mailto:${SALES_CONTACT_EMAIL}?${getEmailContent({ user, action, currentCreditBlocksAmount })}`}
                onPress={() => handleConfirmClick(action)}
              >
                Email Support
              </Button>
              <Button
                className="ml-2"
                component={Link}
                to={`/${ROUTE_PARENT_SECTION}`}
                variant="text"
              >
                Go Back
              </Button>
            </div>

            <Typography
              className="mt-3 text-high-emphasis"
              component="p"
              variant="body"
            >
              Or email
              {' '}
              <span className="font-semibold">{SALES_CONTACT_EMAIL}</span>
              {' '}
              with your Sauce Labs username
              {
                getUpdateText(
                  { action, hideCredits },
                  {
                    update: (
                      ', email address, and requested change.'
                    ),
                    cancel: (
                      ' and email address.'
                    ),
                  })
              }
            </Typography>
            {
              getUpdateText(
                { action, hideCredits },
                {
                  update: (
                    <Typography
                      className="mt-2 text-high-emphasis"
                      component="p"
                      variant="body"
                    >
                      By requesting a credit change with billing@saucelabs.com,
                      you’re authorizing Sauce Labs to modify your monthly plan
                      and to charge your payment method on file for any additional credits.
                      Moving forward, you’ll be charged for the new total requested credits
                      on each month’s billing date until you change or cancel your plan.
                    </Typography>
                  ),
                  updateNoCredits: (
                    <Typography
                      className="mt-2 text-high-emphasis"
                      component="p"
                      variant="body"
                    >
                      By requesting a change with billing@saucelabs.com,
                      you’re authorizing Sauce Labs to modify your plan
                      and to charge your payment method.
                      Moving forward, you’ll be charged for the new
                      requested plan on each billing date until you change or cancel your plan.
                    </Typography>
                  ),
                },
              )
            }
          </div>
        </Col>
      </Row>
    </div>
  </>
);
